import { useState, useCallback, useMemo } from 'react';
import type { SurveyInstance, HomeQuestion } from '@/types/survey.types';

const PAGE_SIZE = 5;

export type QuizAnswer = string | number | null;

/** True when the question should render as a per-participant matrix */
export function isMatrixQuestion(q: HomeQuestion): boolean {
  return q.enfoque === 'individual' && q.presentMode === 'matrix' && q.type !== 'nps';
}

/**
 * Serializes quiz answers to the flat Record<key, value> shape expected by the API.
 * - Regular / individual+individual questions → key = effectiveId (e.g. "q1_p0")
 * - individual+matrix questions              → key = "${questionId}__p${participantIdx}"
 */
export function serializeAnswers(
  effectiveQuestions: EffectiveQuestion[],
  answers: AnyAnswer[],
): Record<string, string | number> {
  const result: Record<string, string | number> = {};
  effectiveQuestions.forEach((eq, i) => {
    const ans = answers[i];
    if (isMatrixQuestion(eq.question)) {
      (ans as QuizAnswer[]).forEach((v, pIdx) => {
        if (v !== null && v !== undefined) {
          result[`${eq.question.id}__p${pIdx}`] = v;
        }
      });
    } else if (ans !== null && ans !== undefined) {
      result[eq.effectiveId] = ans as string | number;
    }
  });
  return result;
}

/** A question as it appears in the quiz — individual+individual gets one copy per participant */
export interface EffectiveQuestion {
  question: HomeQuestion;
  participant?: string;    // for individual+individual only
  participantIdx?: number;
  effectiveId: string;
}

/**
 * answers[eIdx]:
 *  - regular question      → QuizAnswer  (number | string | null)
 *  - individual+matrix     → QuizAnswer[] (one per participant, length = participants.length)
 */
export type AnyAnswer = QuizAnswer | QuizAnswer[];

export interface InlineQuizReturn {
  effectiveQuestions: EffectiveQuestion[];
  answers: AnyAnswer[];
  participants: string[];
  setAnswer: (eIdx: number, value: QuizAnswer) => void;
  setMatrixAnswer: (eIdx: number, participantIdx: number, value: QuizAnswer) => void;
  canAdvance: boolean;
  nextPage: () => void;
  prevPage: () => void;
  isLastPage: boolean;
  page: number;
  totalPages: number;
  pageStart: number;
  visibleQuestions: EffectiveQuestion[];
}

/** Expand raw questions — individual+individual → one copy per participant; matrix → single entry */
function expandQuestions(questions: HomeQuestion[], participants: string[]): EffectiveQuestion[] {
  const result: EffectiveQuestion[] = [];
  for (const q of questions) {
    if (q.enfoque === 'individual' && q.presentMode === 'individual') {
      participants.forEach((name, idx) => {
        result.push({ question: q, participant: name, participantIdx: idx, effectiveId: `${q.id}_p${idx}` });
      });
    } else {
      result.push({ question: q, effectiveId: q.id });
    }
  }
  return result;
}

function initAnswers(effectiveQuestions: EffectiveQuestion[], participantsCount: number): AnyAnswer[] {
  return effectiveQuestions.map(eq =>
    isMatrixQuestion(eq.question)
      ? (new Array(participantsCount).fill(null) as QuizAnswer[])
      : null,
  );
}

function isAnswered(q: HomeQuestion, value: QuizAnswer): boolean {
  if (q.type === 'open') return typeof value === 'string' && value.trim().length >= 2;
  return value !== null;
}

function isMatrixAnswered(q: HomeQuestion, matrixAns: QuizAnswer[]): boolean {
  return matrixAns.every(v => isAnswered(q, v));
}

export function useInlineQuiz(instance: SurveyInstance): InlineQuizReturn {
  const effectiveQuestions = useMemo(
    () => expandQuestions(instance.questions, instance.participants),
    [instance.questions, instance.participants],
  );

  const [answers, setAnswers] = useState<AnyAnswer[]>(
    () => initAnswers(effectiveQuestions, instance.participants.length),
  );
  const [page, setPage] = useState(0);

  const totalPages = Math.max(1, Math.ceil(effectiveQuestions.length / PAGE_SIZE));
  const pageStart = page * PAGE_SIZE;
  const pageEnd = Math.min(pageStart + PAGE_SIZE, effectiveQuestions.length);
  const visibleQuestions = effectiveQuestions.slice(pageStart, pageEnd);
  const isLastPage = page >= totalPages - 1;

  const setAnswer = useCallback((eIdx: number, value: QuizAnswer) => {
    setAnswers(prev => { const next = [...prev]; next[eIdx] = value; return next; });
  }, []);

  const setMatrixAnswer = useCallback((eIdx: number, participantIdx: number, value: QuizAnswer) => {
    setAnswers(prev => {
      const next = [...prev];
      const row = [...(next[eIdx] as QuizAnswer[])];
      row[participantIdx] = value;
      next[eIdx] = row;
      return next;
    });
  }, []);

  const canAdvance = useMemo(() =>
    visibleQuestions.every((eq, i) => {
      const ans = answers[pageStart + i];
      if (isMatrixQuestion(eq.question)) return isMatrixAnswered(eq.question, ans as QuizAnswer[]);
      return isAnswered(eq.question, ans as QuizAnswer);
    }),
    [answers, visibleQuestions, pageStart],
  );

  const nextPage = useCallback(() => setPage(p => Math.min(p + 1, totalPages - 1)), [totalPages]);
  const prevPage = useCallback(() => setPage(p => Math.max(0, p - 1)), []);

  return {
    effectiveQuestions, answers, participants: instance.participants,
    setAnswer, setMatrixAnswer, canAdvance,
    nextPage, prevPage, isLastPage, page, totalPages, pageStart, visibleQuestions,
  };
}
